import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, deletedRefProps, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import { LookupSearchField } from './LookupSearchField';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { locationOptionLabel } from '../LocationTypeAhead';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { ThdOptions, getThdOptions } from '../../services/thd.service';
import { TemplateMatch, searchTemplates } from '../../services/daltile.service';

// Per-option card for a template match (module-level = stable ref). Same card the
// Dal Tile and Melrose template maps use — /daltile/template-search is unscoped, so
// all three share it rather than each growing its own copy.
const renderTemplateOption = (m: TemplateMatch): React.ReactNode => (
  <div className="relative w-full">
    {m.type && <span className="absolute right-0 top-0 text-[0.65625rem] font-bold text-slate-400 dark:text-slate-500">{m.type}</span>}
    <div className="pr-12 text-[0.8125rem] font-bold text-slate-800 dark:text-slate-100">
      {m.template}{m.customer ? <span className="font-normal text-slate-500 dark:text-slate-400"> · {titleCase(m.customer)}</span> : ''}
    </div>
    {m.destination && <div className="text-[0.75rem] text-slate-600 dark:text-slate-300">{titleCase(m.destination)}</div>}
    {(m.movetype || m.mode) && (
      <div className="mt-1 flex flex-wrap gap-x-3 text-[0.6875rem] text-slate-500 dark:text-slate-400">
        {m.movetype && <span>Move: <b className="text-slate-700 dark:text-slate-200">{m.movetype}</b></span>}
        {m.mode && <span>Mode: <b className="text-slate-700 dark:text-slate-200">{m.mode}</b></span>}
      </div>
    )}
  </div>
);
const templateStatus = async (v: string): Promise<string | null> => {
  const rs = await searchTemplates(v);
  return rs.some((r) => r.template.toUpperCase() === v.toUpperCase()) ? null : 'TEMPLATE DELETED';
};

/**
 * Bespoke edit/create drawer for Home Depot Template Mapping
 * (cms_homedepot.templatemap). SCAC / Mode / IDC / Rail / Dispatcher dropdowns, Load
 * CY + Empty CY location typeaheads, and a search-as-you-type Template picker that
 * flags "TEMPLATE DELETED" when the stored one no longer resolves.
 *
 * That last flag is the reason this is not a plain text field. getotw_csv.pl's
 * getTemplateMapping joins `template.deleted='N'`, so a row whose template has been
 * deleted is invisible to the import — the (SCAC, Mode, IDC) combination simply reads
 * as unmapped and nothing says why.
 */
const TABLE = 'thd-templatemap';

const s = (v: any): string => (v == null ? '' : String(v));
const fkSeed = (v: any): string => (v && String(v) !== '0' ? String(v) : '');
const fkOut = (v: any): string => (s(v).trim() || '0');

const ThdTemplateMapEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<ThdOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({
    scac: '', mode: '', dcnumber: '', loadcyid: '', emptycyid: '',
    rail: '', dispatcher: '', template: '',
  });
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getThdOptions().then(setOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  useEffect(() => {
    setForm({
      scac: s(record.scac),
      mode: s(record.mode),
      dcnumber: s(record.dcnumber),
      loadcyid: fkSeed(record.loadcyid),
      emptycyid: fkSeed(record.emptycyid),
      rail: s(record.rail),
      dispatcher: s(record.dispatcher),
      template: s(record.template),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // V1 edittemplatemap's cantbeempty guard, mirrored by _validate_thd_templatemap.
  const errors = useMemo(() => {
    const e: string[] = [];
    if (!s(form.scac).trim()) e.push('SCAC is required.');
    if (!s(form.mode).trim()) e.push('Mode is required.');
    if (!s(form.dcnumber).trim()) e.push('Final Destination (IDC) is required.');
    if (!s(form.template).trim()) e.push('Template is required.');
    return e;
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const dcOpts = useMemo(() => opts?.dcs ?? [], [opts]);
  const modeOpts = useMemo(() => normalizeOptions(opts?.modes ?? []), [opts]);
  const scacOpts = useMemo(() => (opts?.scacs ?? []).map((x) => ({
    value: x.scac, label: x.name ? `${x.scac} - ${titleCase(x.name)}` : x.scac,
  })), [opts]);
  const railOpts = useMemo(() => normalizeOptions(opts?.rails ?? []), [opts]);
  const dispatcherOpts = useMemo(() => opts?.dispatchers ?? [], [opts]);
  const locationOpts = useMemo(() => (opts?.locations ?? []).map((l) => ({
    value: String(l.recno), label: locationOptionLabel(titleCase(s(l.lname)), s(l.cyalias)), labelSuffix: `#${l.recno}`,
    description: [titleCase(s(l.address)), titleCase(s(l.city)), s(l.state)].filter(Boolean).join(', '),
  })), [opts]);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const payload: TablemaintRow = {
      scac: s(form.scac), mode: s(form.mode), dcnumber: s(form.dcnumber),
      loadcyid: fkOut(form.loadcyid), emptycyid: fkOut(form.emptycyid),
      rail: s(form.rail), dispatcher: s(form.dispatcher), template: s(form.template),
    };
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        (['scac', 'mode', 'dcnumber', 'rail', 'dispatcher', 'template'] as const)
          .forEach((k) => { if (s(form[k]) !== s(record[k])) patch[k] = s(form[k]); });
        (['loadcyid', 'emptycyid'] as const)
          .forEach((k) => { if (fkOut(form[k]) !== fkOut(record[k])) patch[k] = fkOut(form[k]); });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save template mapping.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Template Map' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Template Map' : `Edit Template Map (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="52rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <p className="text-xs text-muted">
          SCAC + Mode + Final Destination is the key the OTW import looks up — one mapping
          per combination.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectField label="SCAC" required value={s(form.scac)} onChange={(v) => setField('scac', v)} options={scacOpts} disabled={isSaving} />
          <SelectField label="Mode" required value={s(form.mode)} onChange={(v) => setField('mode', v)} options={modeOpts} disabled={isSaving} />
          <div className="md:col-span-2">
            <SelectField label="Final Destination (IDC)" required value={s(form.dcnumber)} onChange={(v) => setField('dcnumber', v)} options={dcOpts} disabled={isSaving} />
          </div>
          <div className="md:col-span-2">
            <LookupSearchField<TemplateMatch>
              label="Template" required
              displayValue={s(form.template)}
              onType={(t) => setField('template', t)}
              onSelect={(m) => setField('template', m.template)}
              search={searchTemplates} renderItem={renderTemplateOption}
              itemValue={(m) => m.template} itemLabel={(m) => m.template}
              statusValue={s(form.template)} checkStatus={templateStatus}
              placeholder="Type a template # or destination…" disabled={isSaving}
              clearOnBlurWithoutSelect
            />
            <p className="mt-1 text-xs text-muted">
              The import skips a mapping whose template has been deleted — the combination
              above reads as unmapped.
            </p>
          </div>
          <div className="md:col-span-2">
            <SelectField label="Load CY" value={s(form.loadcyid)} onChange={(v) => setField('loadcyid', v)} options={locationOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Load CY', record.loadcyname, form.loadcyid, record.loadcydeleted)} />
          </div>
          <div className="md:col-span-2">
            <SelectField label="Empty CY" value={s(form.emptycyid)} onChange={(v) => setField('emptycyid', v)} options={locationOpts} disabled={isSaving} showDescriptions showSelectedInfo
              {...deletedRefProps('Empty CY', record.emptycyname, form.emptycyid, record.emptycydeleted)} />
          </div>
          <SelectField label="Rail / Port Trace" value={s(form.rail)} onChange={(v) => setField('rail', v)} options={railOpts} disabled={isSaving} />
          <SelectField label="Dispatcher" value={s(form.dispatcher)} onChange={(v) => setField('dispatcher', v)} options={dispatcherOpts} disabled={isSaving}
            {...deletedRefProps('Dispatcher', record.dispatchername, form.dispatcher, record.dispatcherdeleted)} />
        </div>

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <label className="flex items-start gap-3 cursor-pointer select-none py-1">
              <input type="checkbox" checked={formDeleted} disabled={isSaving}
                onChange={(e) => setFormDeleted(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="text-sm font-medium text-label">Deleted</span>
            </label>
            <p className="mt-1 ml-7 text-xs text-muted">
              Soft-deletes this mapping — hidden from filters but preserved in history. Uncheck and save to restore.
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ThdTemplateMapEditDrawer;
